import { Suspense, useMemo, type ReactElement } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Routes } from "react-router-dom";
import { StaticRouter } from "react-router-dom/server";
import { usePageContext } from "vike-react/usePageContext";
import { store } from "@/store/store";
import RouteLoadingFallback from "@/shared/RouteLoadingFallback";
import SiteDocumentTitleSync from "@/components/common/SiteDocumentTitleSync";
import {
  createPublicRoutes,
  renderAppMainRoutes,
  type PublicRouteOverrides,
} from "@/router/appMainRoutes";

type PublicSsrPageOptions = {
  /** Canonical pathname, e.g. `/preguntas-frecuentes` */
  pathname: string;
  /** Route override key in `createPublicRoutes` */
  overrideKey: keyof PublicRouteOverrides;
  /** Page element to render for this SSR route */
  element: ReactElement;
};

export function createPublicSsrPage({
  pathname,
  overrideKey,
  element,
}: PublicSsrPageOptions) {
  return function PublicSsrPage() {
    const pageContext = usePageContext();
    const isServer = typeof window === "undefined";
    const location = pageContext.urlPathname || pathname;

    const publicRoutes = useMemo(
      () => createPublicRoutes({ [overrideKey]: element } as PublicRouteOverrides),
      [element]
    );

    const tree = (
      <Suspense fallback={<RouteLoadingFallback />}>
        <SiteDocumentTitleSync />
        <Routes>{renderAppMainRoutes({ publicRoutes })}</Routes>
      </Suspense>
    );

    return (
      <Provider store={store}>
        {isServer ? (
          <StaticRouter location={location}>{tree}</StaticRouter>
        ) : (
          <BrowserRouter>{tree}</BrowserRouter>
        )}
      </Provider>
    );
  };
}
